import {
  HttpClient,
  HttpContext,
  HttpContextToken
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, of, take } from 'rxjs';
import {
  UsersPasswordResetConfirmRequestDto,
  UsersPasswordResetRequestDto,
  UsersVerifyResetCodeResponseDto
} from 'src/app/shared/vive-api/vive-api-models';

import { Contact, User, UserAccountRole } from '../classes/models';
import { GenericObject } from '../interfaces/generic-object';
import { UserAccountType } from '../interfaces/models/user-account-type';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  public checkForDuplicateUsers(query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get(`/api/v2/admin/user/checkForDuplicateUsers`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getUsersV2(query = {}): Observable<User[]> {
    return this.http.get<User[]>(`/api/v2/user/many`, { params: query });
  }

  public getUserV2(query = {}): Observable<User> {
    return this.http.get<User>(`/api/v2/user/one`, { params: query });
  }

  public getUserCountV2(query = {}): Observable<number> {
    return this.http.get<number>(`/api/v2/user/count`, { params: query });
  }

  public getUserAccountTypeForUser(
    email: string,
    query = {}
  ): Observable<UserAccountType | null> {
    return this.http.get<UserAccountType | null>(
      `/api/v2/user/accountType/${email}`,
      {
        params: query
      }
    );
  }

  public getUserAccountRoles(query = {}): Observable<UserAccountRole[]> {
    return this.http.get<UserAccountRole[]>(
      `/api/v2/user/userAccountRole/many`,
      { params: query }
    );
  }

  public getAllUserIds(): Observable<any> {
    return this.http.get(`/api/v2/user/getAllUserIds`);
  }

  saveUsers(Users: User[], query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(`/api/v2/admin/user/many`, Users, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  // Need to set this up. Vendors need to be able to create a User first then a Contact record
  // (though both can be done with a single route). Need to set this up similar to the address
  // routes to check for User Type.
  public saveUser(User: User, query = {}, optional: GenericObject = {}) {
    // Can move this to utility service if it becomes a common pattern (it most likely will).
    const { manuallyHandleError } = optional;
    const context = new HttpContext();
    if (manuallyHandleError) {
      const ContextToken = new HttpContextToken(() => 'ManuallyHandleError');
      context.set(ContextToken, null);
    }

    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<User>;
        } = {
          Vendor: () =>
            this.http.post(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/user`,
              User,
              { params: query, context }
            ),
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/user`,
              User,
              { params: query, context }
            ),
          Admin: () =>
            this.http.post(`/api/v2/admin/user`, User, {
              params: query,
              context
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  public mapUserToContact = (User: User): Contact => {
    const {
      id,
      CompanyId = undefined,
      VendorId = undefined,
      firstName,
      lastName,
      phone,
      extension,
      email,
      jobTitle,
      deleted,
      ContactId
    } = User;

    return {
      id: ContactId || undefined,
      CompanyId,
      VendorId,
      firstName,
      lastName,
      email,
      phone1: phone,
      ext: extension,
      jobTitle,
      isArchived: deleted || undefined,
      UserId: id
    };
  };

  public requestPasswordReset(
    body: UsersPasswordResetRequestDto
  ): Observable<void> {
    return this.http.post<void>('/api/v3/users/password-reset', body);
  }

  // The code comes from the reset link's URL, and this request carries the
  // session cookie, so the code must stay a single path segment: it is encoded,
  // which covers `/`, and `.` and `..` are refused outright because encoding
  // leaves them as dot segments the browser resolves to a different API path.
  // No issued code has that form, so the answer is "unusable" without a call.
  public verifyPasswordResetCode(
    code: string
  ): Observable<UsersVerifyResetCodeResponseDto> {
    if (code === '.' || code === '..') {
      return of({ valid: false });
    }

    return this.http.get<UsersVerifyResetCodeResponseDto>(
      `/api/v3/users/password-reset/${encodeURIComponent(code)}`
    );
  }

  public confirmPasswordReset(
    body: UsersPasswordResetConfirmRequestDto
  ): Observable<void> {
    return this.http.post<void>('/api/v3/users/password-reset/confirm', body);
  }
}
